/**
 * Pure reducer for when the diagnosis popover shows (plan 15.4 "When it appears", 16.4).
 *
 *   hidden in hybrid; the first diagnosis state of a session opens it small (or a pulsing bubble when the
 *   user last minimised it); the bubble counts updates (top cause moved >= 1 pt, or a new pending
 *   question) and the count clears on open; the diagnosis ending collapses it to the bubble (frozen,
 *   still readable); the Cases/Bugs overlay collapses it to the bubble and restores it on close; a new
 *   session or clear hides it. The enterprise variant never shows it (O9, 16.1).
 * FloatingPanel's own transitions (minimise, maximise, close, Esc, bubble click) arrive as PANEL.
 */
import { PANEL_STATES } from "./floatingPanelConstants";
import { bubbleSignal, isEnded } from "./brainStateLogic";

const { HIDDEN, BUBBLE, SMALL, MAXIMISED } = PANEL_STATES;
const OPEN_STATES = new Set([SMALL, MAXIMISED]);

export const POPOVER_ACTIONS = Object.freeze({
  DIAG_STATE: "diag_state",
  PANEL: "panel",
  OVERLAY: "overlay",
  OPEN: "open",
  RESET: "reset",
});

export const initialPopover = Object.freeze({
  panel: HIDDEN,
  appeared: false,
  collapsedForOverlay: false,
  updates: 0,
  pulse: false,
});

function onDiagState(s, { prev, next, preferBubble, enterprise }) {
  if (enterprise || !next) return s;
  if (!s.appeared) {
    const asBubble = isEnded(next) || preferBubble;
    return { ...s, appeared: true, panel: asBubble ? BUBBLE : SMALL, pulse: asBubble && !isEnded(next), updates: 0 };
  }
  let panel = s.panel;
  const justEnded = isEnded(next) && !isEnded(prev && prev.diagnosis_id === next.diagnosis_id ? prev : null);
  if (justEnded && OPEN_STATES.has(panel)) panel = BUBBLE;
  if (panel === HIDDEN) panel = BUBBLE;
  const { pctMoved, newPending } = bubbleSignal(prev, next);
  const collapsed = panel === BUBBLE;
  const signal = collapsed && (pctMoved || newPending || (prev && prev.diagnosis_id !== next.diagnosis_id));
  return {
    ...s,
    panel,
    updates: signal ? s.updates + 1 : s.updates,
    pulse: signal ? true : s.pulse,
  };
}

function onPanel(s, { next }) {
  const opened = OPEN_STATES.has(next);
  return {
    ...s,
    panel: next,
    collapsedForOverlay: opened ? false : s.collapsedForOverlay,
    updates: opened ? 0 : s.updates,
    pulse: opened ? false : s.pulse,
  };
}

function onOverlay(s, { open }) {
  if (open) {
    return OPEN_STATES.has(s.panel) ? { ...s, panel: BUBBLE, collapsedForOverlay: true } : s;
  }
  if (!s.collapsedForOverlay) return s;
  return { ...s, panel: s.panel === BUBBLE ? SMALL : s.panel, collapsedForOverlay: false, updates: 0, pulse: false };
}

export function popoverReducer(state, action) {
  switch (action.type) {
    case POPOVER_ACTIONS.DIAG_STATE:
      return onDiagState(state, action);
    case POPOVER_ACTIONS.PANEL:
      return onPanel(state, action);
    case POPOVER_ACTIONS.OVERLAY:
      return onOverlay(state, action);
    case POPOVER_ACTIONS.OPEN:
      if (action.enterprise) return state;
      return { ...state, appeared: true, panel: OPEN_STATES.has(state.panel) ? state.panel : SMALL, collapsedForOverlay: false, updates: 0, pulse: false };
    case POPOVER_ACTIONS.RESET:
      return initialPopover;
    default:
      return state;
  }
}

const OPEN_LIFECYCLES = ["active", "paused"];

/** True while the diagnosis of this diag_state is running or paused for a side question (not ended). */
export const diagnosisOpen = (diagState) => OPEN_LIFECYCLES.includes(diagState?.lifecycle);
